import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { patchReplace } from "../../../helpers/patchDoc";
import {
    useCreateContactDetailsMutation,
    useDeleteContactDetailsMutation,
    useFetchMasterAccountContactDetailsQuery,
    usePatchContactDetailsMutation
} from "../../../services/clientdetails";

export const ContactTypes = {
    Phone: {
        Landline: 0,
        Mobile: 1,
        Fax: 3
    },
    Email: {
        Email: 2,
        'Restricted Email': 4
    }
}

const getSuperType = (type) =>
    Object.keys(ContactTypes).find(superType =>
        Object.values(ContactTypes[superType]).includes(type)
    );

const useContactDetails = (masterAccountId) => {
    const { data, error, isError, isFetching, isLoading, refetch } = useFetchMasterAccountContactDetailsQuery({ masterAccountId, sort: 'isDefault+Desc,createdDate' });
    const [create, { error: createError, isError: isErrorCreating, isLoading: isCreating }] = useCreateContactDetailsMutation();
    const [remove, { error: removeError, isError: isErrorRemoving, isLoading: isRemoving }] = useDeleteContactDetailsMutation();
    const [patch, { error: updateError, isError: isErrorUpdating, isLoading: isUpdating }] = usePatchContactDetailsMutation();

    const [isCreatingType, setIsCreatingType] = useState(null);

    const createContactDetails = (newSuperType) => {
        return toast.promise(
            new Promise((resolve, reject) => {
                if (!newSuperType) {
                    return reject('Invalid contact type');
                }

                setIsCreatingType(newSuperType);

                // setup this contact details as the default when there is no other default set matching its type
                let isDefault = false;
                if (!isLoading && !isError && data) {
                    let defaults = data.filter(el => el.isDefault === true && getSuperType(el.type) === newSuperType);
                    isDefault = defaults.length === 0;
                }

                let newDetails = {
                    type: Object.values(ContactTypes[newSuperType])[0],
                    details: '',
                    description: '',
                    isDefault: isDefault,
                    isActiveDetails: true
                };

                return create({ masterAccountId, ...newDetails }).unwrap().then(
                    r => {
                        setIsCreatingType(null);
                        resolve(r);
                    },
                    e => reject(e)
                );
            }),
            {
                loading: 'Creating contact details...',
                success: 'Contact details created',
                error: (err) => err.data?.message || 'Error creating contact details'
            }
        ).finally(() => setIsCreatingType(null));
    };

    const deleteContactDetails = (contactDetailId) => {
        return toast.promise(
            remove({ contactDetailId, masterAccountId }).unwrap(),
            {
                loading: 'Deleting contact details...',
                success: 'Contact details deleted',
                error: (err) => err.data?.message || 'Error deleting contact details'
            }
        )
    };

    const reload = (e) => {
        if (e && typeof (e.preventDefault) === 'function') {
            e.preventDefault();
        }
        refetch({ masterAccountId });
    };

    const setDefaultContactDetails = (contactDetailId) => {
        return new Promise((resolve, reject) => {
            // generate all the options we want to push up to the api
            let requests = [];

            // Find the contact in question so we can check its type and update others of the same supertype
            const contactDetail = data.find(el => el.contactDetailId === contactDetailId);

            if (!contactDetail)
                return reject('Contact detail not found - Please refresh the page.');

            const { type, isDefault } = contactDetail;
            if (isDefault === false)
                requests.push({
                    contactDetailId,
                    operations: [
                        patchReplace("isDefault", true)
                    ]
                });

            data.forEach(el => {
                if (el.contactDetailId === contactDetailId)
                    return;

                if (el.isDefault === true && getSuperType(el.type) === getSuperType(type)) {
                    requests.push({
                        contactDetailId: el.contactDetailId,
                        operations: [
                            patchReplace("isDefault", false)
                        ]
                    });
                }
            });

            return patch({ masterAccountId, operations: requests }).unwrap().then(
                r => resolve(r),
                e => reject(e)
            );
        });
    };

    const updateContactDetails = (contactDetailId, property, value) => {
        return new Promise((resolve, reject) => {
            let operations = [
                patchReplace(property, value)
            ];

            let request = [{
                contactDetailId,
                operations
            }];

            return patch({ masterAccountId, operations: request }).unwrap().then(
                r => resolve(r),
                e => reject(e)
            );
        });
    };

    // Convert the data into our table format with Phone Email headers
    const tableRows = useMemo(() => {
        if (!data || data.length === 0) {
            return [];
        }

        let rows = [];
        Object.keys(ContactTypes).forEach(superType => {

            // Get the contact type entries for this "superType" and map into an options array for the select
            let subTypes = Object.entries(ContactTypes[superType]);
            let subTypeOptions = subTypes.map(entry => ({
                value: entry[1],
                label: entry[0]
            }));

            // Find the contact details of this superType and add our information for the row render
            let typeData = data.filter(el => subTypes
                .map(entry => entry[1])
                .includes(el.type)
            ).map(el => ({
                ...el,
                superType,
                typeOptions: subTypeOptions
            }));

            // Add a header row for each type of contact
            if (typeData.length > 0 || superType === isCreatingType) {
                rows.push({
                    type: superType
                });

                // followed by the contact details of that type
                rows.push(...typeData);
            }

            // and then a loading row (represented by null) if we are creating a new contact of that type
            if (superType === isCreatingType)
                rows.push(null);
        });

        return rows;
    }, [data, isCreatingType]);

    return {
        createContactDetails,
        createError,
        tableRows,
        deleteContactDetails,
        deleteError: removeError,
        error,
        isError,
        isErrorCreating,
        isErrorRemoving,
        isErrorUpdating,
        isCreating,
        isLoading: isLoading === true || isFetching === true,
        isRemoving,
        isUpdating,
        reload,
        removeError,
        setDefaultContactDetails,
        updateError,
        updateContactDetails
    };
};

export default useContactDetails;